import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import StatusBadge from "../components/StatusBadge";
import { toast, Toaster } from "sonner";
import { Printer, FileXls, FileText, Warehouse } from "@phosphor-icons/react";

const TYPES = [
  { value: "all", label: "Full Report" },
  { value: "discrepancy", label: "Discrepancy Only" },
  { value: "escalated", label: "Escalated" },
  { value: "finalized", label: "Finalized by Head Counter" },
];

export default function AdminReports() {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);

  const [mode, setMode] = useState("main"); // main | alley
  const [type, setType] = useState("all");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/sessions").then(({ data }) => {
      setSessions(data);
      if (!sessionId && data[0]) {
        setSessionId(data[0].id);
        setSelectedSessionIds([data[0].id]);
      }
    });
  }, []);

  const run = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    try {
      if (mode === "main") {
        const { data } = await api.get(`/reports/${sessionId}`, { params: { type } });
        setData(data);
      } else {
        const { data } = await api.get(`/reports/${sessionId}/alley`);
        setData(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [sessionId, type, mode]);

  useEffect(() => {
    run();
  }, [run]);

  const exportMain = async () => {
    const resp = await api.get(`/reports/${sessionId}/export`, {
      params: { type },
      responseType: "blob",
    });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `main_report.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAlley = async (onlyDiscrepancies = false) => {
    const resp = await api.get(`/reports/${sessionId}/alley/export`, {
      params: { only_discrepancies: onlyDiscrepancies },
      responseType: "blob",
    });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = onlyDiscrepancies
      ? "alley_discrepancy.xlsx"
      : "alley_full.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSession = (id) => {
    setSelectedSessionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const exportCombined = async (onlyDiscrepancies = false) => {
    const ids = selectedSessionIds.length ? selectedSessionIds : [sessionId];

    if (!ids.length || !ids[0]) {
      toast.error("Select at least one session");
      return;
    }

    const resp = await api.get("/combined-reports/export", {
      params: {
        session_ids: ids.join(","),
        report_mode: mode,
        type,
        only_discrepancies: onlyDiscrepancies,
      },
      responseType: "blob",
    });

    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = mode === "alley"
      ? "combined_alley_report.xlsx"
      : "combined_main_report.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppShell>
      <Toaster position="top-right" richColors />

      <div className="max-w-[1600px] mx-auto p-4 space-y-5">

        {/* HEADER */}
        <div className="flex justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Reports</h1>
            <p className="text-sm text-zinc-600">Main vs Alley reconciliation reports</p>
          </div>

          <div className="flex gap-2">
            <button onClick={() => window.print()} className="border px-3 py-2 text-sm">
              <Printer size={16}/> Print
            </button>

            {mode === "main" && (
              <>
                <button onClick={exportMain} className="bg-indigo-900 text-white px-3 py-2 text-sm">
                  <FileXls size={16}/> Export Current
                </button>
                <button onClick={() => exportCombined(false)} className="bg-emerald-700 text-white px-3 py-2 text-sm">
                  Export Combined
                </button>
              </>
            )}

            {mode === "alley" && (
              <>
                <button onClick={() => exportAlley(false)} className="bg-indigo-900 text-white px-3 py-2 text-sm">
                  Export Current Alley
                </button>
                <button onClick={() => exportCombined(false)} className="bg-emerald-700 text-white px-3 py-2 text-sm">
                  Export Combined Alley
                </button>
                <button onClick={() => exportCombined(true)} className="bg-red-700 text-white px-3 py-2 text-sm">
                  Export Combined Discrepancy
                </button>
              </>
            )}
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="border p-3 grid grid-cols-3 gap-2">
          <select value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>{s.session_name}</option>
            ))}
          </select>

          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="main">Main Report</option>
            <option value="alley">Alley Report</option>
          </select>

          {mode === "main" && (
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          )}
        </div>

        <div className="border bg-white p-3">
          <div className="font-semibold text-sm mb-2">Sessions to combine</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 max-h-52 overflow-auto">
            {sessions.map((s) => (
              <label key={s.id} className="flex items-start gap-2 text-sm border rounded px-2 py-1">
                <input
                  type="checkbox"
                  checked={selectedSessionIds.includes(s.id)}
                  onChange={() => toggleSession(s.id)}
                />
                <span>
                  <span className="font-medium">{s.session_name}</span>
                  <span className="block text-xs text-zinc-500">{s.warehouse_name || "—"} · {s.item_count} items</span>
                </span>
              </label>
            ))}
          </div>
          <div className="text-xs text-zinc-500 mt-2">
            Selected: {selectedSessionIds.length || 0}
          </div>
        </div>

        {/* TABLE */}
        <div className="border bg-white p-3 overflow-auto">

          {mode === "main" && data && (
            <table className="data-table text-sm">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Item</th>
                  <th>System</th>
                  <th>Collective</th>
                  <th>Discrepancy</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i}>
                    <td><StatusBadge status={r.reconciliation?.status}/></td>
                    <td>{r.item.item_code}</td>
                    <td>{r.item.system_count}</td>
                    <td>{r.reconciliation?.collective_count}</td>
                    <td>{r.reconciliation?.discrepancy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {mode === "alley" && data && (
            <table className="data-table text-sm">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>System</th>
                  <th>Final Count</th>
                  <th>Difference</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.item_number}</td>
                    <td>{r.system_quantity}</td>
                    <td>{r.alley_final_count}</td>
                    <td>{r.difference}</td>
                    <td>{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

        </div>

        {loading && <div>Loading...</div>}

      </div>
    </AppShell>
  );
}
